
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, Calendar as CalendarIcon } from "lucide-react";
import { format, isSameDay } from "date-fns";
import { useAvailableTimeSlots } from "@/hooks/useAvailableTimeSlots";
import { Service } from "@/hooks/useServices";

interface AvailabilityCalendarProps {
  providerId: string;
  services: Service[];
  selectedService?: string;
}

const AvailabilityCalendar = ({ providerId, services, selectedService }: AvailabilityCalendarProps) => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  // Determine which service to show - undefined means "all services"
  const serviceToShow = selectedService === "all" || !selectedService
    ? undefined 
    : selectedService;

  // Pass isProviderViewing=true to get simplified availability logic
  // When serviceToShow is undefined, pass providerId to fetch all provider's slots
  const { data: availableSlots, isLoading } = useAvailableTimeSlots(
    serviceToShow, 
    selectedDate, 
    true,
    serviceToShow === undefined ? providerId : undefined
  );

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
    }
  };

  const formatTimeSlot = (startTime: string, endTime: string) => {
    const start = format(new Date(startTime), 'HH:mm');
    const end = format(new Date(endTime), 'HH:mm');
    return `${start} - ${end}`;
  };

  const selectedServiceName = serviceToShow 
    ? services.find(s => s.id === serviceToShow)?.name || "Unknown Service"
    : "All Services";

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calendar */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <CalendarIcon className="h-5 w-5" />
              <span>Select Date</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              className="rounded-md border pointer-events-auto"
            />
          </CardContent>
        </Card>

        {/* Available Time Slots */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Clock className="h-5 w-5" />
              <span>Time Slot Availability</span>
            </CardTitle>
            <p className="text-sm text-gray-600 mt-1">
              {format(selectedDate, 'EEEE, MMMM dd, yyyy')} - {selectedServiceName}
            </p>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading time slots...</p>
              </div>
            ) : availableSlots && availableSlots.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {availableSlots.map((slot) => {
                  const spotsLeft = slot.max_bookings - slot.current_bookings;
                  const isFullyBooked = spotsLeft === 0;
                  
                  return (
                    <div
                      key={slot.id}
                      className={`flex items-center justify-between p-3 border rounded-lg ${
                        isFullyBooked ? 'bg-gray-50 opacity-75' : 'bg-white hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <Clock className="h-4 w-4 text-gray-500 flex-shrink-0" />
                        <div>
                          <div className="font-medium">
                            {formatTimeSlot(slot.start_time, slot.end_time)}
                          </div>
                          <div className="text-sm text-gray-600">
                            {slot.service?.name || 'Unknown Service'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge 
                          variant={spotsLeft > 0 ? "default" : "secondary"}
                          className={
                            spotsLeft > 0 
                              ? "bg-green-100 text-green-800 border-green-300" 
                              : "bg-red-100 text-red-800 border-red-300"
                          }
                        >
                          {spotsLeft} / {slot.max_bookings} available
                        </Badge>
                        {slot.womens_only && (
                          <Badge variant="outline" className="text-xs">
                            Women Only
                          </Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No time slots found</p>
                <p className="text-sm mt-2">
                  {selectedService === "all" 
                    ? "No time slots available for this date"
                    : "No time slots available for this service on this date"
                  }
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Summary Stats */}
      {availableSlots && availableSlots.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-600">
                  {availableSlots.length}
                </div>
                <div className="text-sm text-gray-600">Total Slots</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">
                  {availableSlots.reduce((sum, slot) => sum + (slot.max_bookings - slot.current_bookings), 0)}
                </div>
                <div className="text-sm text-gray-600">Available Spots</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-orange-600">
                  {availableSlots.reduce((sum, slot) => sum + slot.current_bookings, 0)}
                </div>
                <div className="text-sm text-gray-600">Booked Spots</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AvailabilityCalendar;
